/**
 * PrintShell — shared layout for all document print pages.
 *
 * Renders a toolbar (Close + Print buttons, hidden when printing), an A4-sized white
 * canvas, hospital-branded header/footer, and auto-fires window.print() ~800ms after
 * data is ready. Resolves the document's tenant via `hospitalId` (preferred over the
 * user's current auth hospital) so Super Admin prints carry the correct hospital
 * branding.
 *
 * Usage:
 *   <PrintShell
 *     title="Discharge Summary"
 *     subtitle="Patient discharge document"
 *     loading={loading}
 *     hospitalId={record?.hospital_id}
 *     docRef={`DIS-${id.slice(0,8)}`}
 *     statusPill={{ label: 'Final', color: '#10b981' }}>
 *       …document body…
 *   </PrintShell>
 */
import { useEffect, useState } from 'react';
import { Printer, ArrowLeft, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth';

export const PrintShell = ({
  title,
  subtitle,
  loading,
  notFound,
  hospitalId,
  docRef,
  statusPill,
  children,
}) => {
  const { hospital: authHospital } = useAuth();
  const [tenantHospital, setTenantHospital] = useState(null);
  const [autoPrinted, setAutoPrinted] = useState(false);

  // Resolve correct tenant if it differs from the logged-in user's hospital (Super Admin flow)
  useEffect(() => {
    if (!hospitalId || hospitalId === authHospital?.id) return;
    let mounted = true;
    api.get(`/hospitals/${hospitalId}`)
      .then(res => { if (mounted) setTenantHospital(res.data); })
      .catch(() => { /* fallback to authHospital */ });
    return () => { mounted = false; };
  }, [hospitalId, authHospital?.id]);

  const hospital = tenantHospital || authHospital || {};
  const branding = hospital.branding || {};
  const primary = branding.primary_color || '#059669';

  // Auto-print once content is ready
  useEffect(() => {
    if (!loading && !notFound && !autoPrinted && children) {
      const t = setTimeout(() => { window.print(); setAutoPrinted(true); }, 800);
      return () => clearTimeout(t);
    }
  }, [loading, notFound, autoPrinted, children]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400 gap-2">
        <Loader2 className="animate-spin" size={20} /> Loading {title?.toLowerCase() || 'document'}…
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="print-notfound">
        Record not found or access denied.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 print:bg-white">
      {/* Toolbar — hidden when printing */}
      <div className="print:hidden bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between sticky top-0 z-10">
        <button onClick={() => window.close()} className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1.5" data-testid="print-back-btn">
          <ArrowLeft size={12} /> Close
        </button>
        <h1 className="text-sm font-semibold text-slate-700 hidden md:block">{title} · Print Preview</h1>
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-md text-white"
          style={{ background: primary }}
          data-testid="print-trigger-btn">
          <Printer size={12} /> Print
        </button>
      </div>

      {/* Document canvas (A4) */}
      <div className="max-w-3xl mx-auto bg-white p-10 print:p-8 my-6 print:my-0 print:shadow-none shadow-sm border border-slate-200 print:border-0" data-testid="print-doc">
        {/* Branded header */}
        <div className="flex items-start justify-between border-b-2 pb-5 mb-6" style={{ borderColor: primary }}>
          <div className="flex items-start gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={hospital.name} className="h-14 w-14 object-contain" />
            ) : (
              <div className="h-14 w-14 rounded-lg flex items-center justify-center text-white font-bold text-xl" style={{ background: primary }}>
                {(hospital.name || 'K').charAt(0)}
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold text-slate-900">{hospital.name || 'Klick Niramaya HMS'}</h1>
              {hospital.address && <p className="text-[10px] text-slate-500 mt-0.5 max-w-xs leading-relaxed">{hospital.address}</p>}
              <p className="text-[10px] text-slate-500 mt-0.5">
                {hospital.phone && <>📞 {hospital.phone}</>}
                {hospital.phone && (hospital.email || hospital.support_email) && ' · '}
                {(hospital.email || hospital.support_email) && <>✉ {hospital.email || hospital.support_email}</>}
              </p>
            </div>
          </div>
          <div className="text-right">
            {statusPill && (
              <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded text-white" style={{ background: statusPill.color }}>
                {statusPill.label}
              </div>
            )}
            {docRef && (
              <>
                <p className="mt-3 text-[10px] text-slate-500">Document Ref</p>
                <p className="text-sm font-mono font-bold text-slate-900">{docRef}</p>
              </>
            )}
            <p className="text-[10px] text-slate-500 mt-1">{new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
          </div>
        </div>

        {title && <h2 className="text-base font-bold text-slate-800 mb-1">{title}</h2>}
        {subtitle && <p className="text-[11px] text-slate-500 mb-5">{subtitle}</p>}

        {children}

        {/* Footer */}
        <div className="mt-10 pt-4 border-t border-slate-100 text-center">
          <p className="text-[9px] text-slate-400">
            System-generated document from {hospital.name || 'Klick Niramaya HMS'}
            {docRef && <> · {docRef}</>}
          </p>
          <p className="text-[9px] text-slate-400 mt-0.5">Powered by Klick Niramaya HMS · Printed on {new Date().toLocaleString('en-IN')}</p>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
};

export const PrintField = ({ label, value, mono }) => (
  <div>
    <p className="text-[9px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
    <p className={`text-xs text-slate-800 mt-0.5 ${mono ? 'font-mono' : ''}`}>{value || '—'}</p>
  </div>
);

export const PrintSection = ({ title, children }) => (
  <section className="mb-5">
    <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 pb-1 border-b">{title}</h3>
    {children}
  </section>
);

export const PrintSignatures = ({ left = 'Authorised Signatory', right = 'Patient / Guardian', leftLabel, rightLabel }) => (
  <div className="grid grid-cols-2 gap-12 mt-12 pt-8 border-t border-slate-200">
    <div className="text-center">
      <div className="h-12 border-b border-slate-400 mb-2" />
      <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide">{left}</p>
      {leftLabel && <p className="text-[9px] text-slate-400">{leftLabel}</p>}
    </div>
    <div className="text-center">
      <div className="h-12 border-b border-slate-400 mb-2" />
      <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide">{right}</p>
      {rightLabel && <p className="text-[9px] text-slate-400">{rightLabel}</p>}
    </div>
  </div>
);

export const fmtINR = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const num = Number(v);
  if (Number.isNaN(num)) return v;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(num);
};

export const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

export const fmtDateTime = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

export default PrintShell;
